'use client';

import { MobileSelect, SelectItem } from '@florencelabs/select';
import React, { ReactElement } from 'react';
import type {
  NearestSlot,
  NearestAvailability,
} from '@provider-directory/components/availability/availability.model';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import {
  Selector,
  SelectorItem,
} from '@provider-directory/components/selector/selector.component';
import { getLocationDisplayInfo } from '@provider-directory/functions/location';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { LocationResource } from '@provider-directory/types/locations.contracts';
import { LocationSelectorAdditionalInfo } from './location-selector-additional-info.component';
import './location-selector-mobile.css';

export const createLocationOption = (
  location: LocationResource,
  nearestAvailability: NearestAvailability | null = null,
): LocationOption => {
  const { displayName, addressLabel } = getLocationDisplayInfo(location);
  return {
    label: addressLabel,
    title: displayName,
    value: location.ehrExternalId || '',
    isPrimaryLocation: location.isPrimaryLocation || false,
    availability: location.availability ?? 0,
    nearestSlot: nearestAvailability?.byLocation[location.ehrExternalId ?? ''],
  };
};

export interface LocationOption {
  label: string;
  value: string;
  title?: string;
  nearestSlot?: NearestSlot;
  isPrimaryLocation: boolean;
  availability: number;
}

export interface LocationSelectorProps {
  locations: LocationOption[];
  location: string;
  name?: string;
  onChange: (location: string) => void;
  defaultKey?: string;
  mobileDrawerTitle?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const getLocationItemDisplayData = (location: LocationOption) => {
  const displayName =
    location.title && location.title.trim().length > 0
      ? location.title
      : location.label.split(',')[0];

  const description = location.label.replace(
    new RegExp(`^${displayName},?`),
    '',
  );
  const testId = displayName
    ? `location-${displayName.replace(/\s+/g, '').toLowerCase()}`
    : undefined;

  return { displayName, description, testId };
};

const LocationSelector = (props: LocationSelectorProps): ReactElement => {
  const {
    locations,
    name,
    onChange,
    location,
    mobileDrawerTitle,
    isDisabled,
    ariaLabel,
    ariaLabelledBy,
  } = props;

  const isDesktop = useIsDesktop();

  if (props.isLoading) {
    return <LoadingGradient className={'h-[64px] w-full rounded-sm'} />;
  }

  const handleSelectionChange = (key: React.Key | null) => {
    const selectedLocation = locations.find((lc) => lc.value === key);
    if (!selectedLocation) return;

    onChange(selectedLocation.value);
  };

  if (isDesktop) {
    return (
      <Selector
        data-testid={'location-selector'}
        name={name}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        items={locations}
        clearable={false}
        selectedKey={location}
        onSelectionChange={handleSelectionChange}
        buttonClassName={'bg-white'}
        isDisabled={isDisabled}
      >
        {locations.map((lt) => {
          const { displayName, description, testId } =
            getLocationItemDisplayData(lt);
          return (
            <SelectorItem
              data-testid={testId ?? null}
              id={lt.value}
              key={lt.value}
              value={lt}
              textValue={displayName}
              className={'text-body-small-semibold'}
            >
              {/* Hand-rolled in place of SelectorItemContent: additionalInfo
                  needs the colored availability text below, but the published
                  component's additionalInfo slot only accepts a string. */}
              <div className={'flex w-full flex-1 flex-col text-left'}>
                <span className={'fab-list-box-item-content-label'}>
                  {displayName}
                </span>
                <span
                  className={
                    'fab-list-box-item-content-description text-black/60'
                  }
                >
                  {description && (
                    <span className={'block'}>{description}</span>
                  )}
                  <span
                    className={
                      'fab-list-box-item-content-additional-info block'
                    }
                  >
                    <LocationSelectorAdditionalInfo
                      nearestSlot={lt.nearestSlot}
                    />
                  </span>
                </span>
              </div>
            </SelectorItem>
          );
        })}
      </Selector>
    );
  }

  return (
    <MobileSelect
      testId={'location-selector'}
      name={name}
      label={mobileDrawerTitle}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      items={locations}
      clearable={false}
      selectedKey={location}
      onSelectionChange={handleSelectionChange}
      isDisabled={isDisabled}
      className={'location-selector-mobile'}
    >
      {locations.map((lt) => {
        const { displayName, description, testId } =
          getLocationItemDisplayData(lt);
        return (
          <SelectItem
            data-testid={testId}
            id={lt.value}
            key={lt.value}
            textValue={displayName}
            isMobile={true}
            className={'!overflow-visible !whitespace-normal'}
          >
            <div className={'flex flex-col'}>
              <span className={'fab-list-box-item-content-label font-bold'}>
                {displayName}
              </span>
              {description && (
                <span className={'truncate text-body-small text-gray-600'}>
                  {description}
                </span>
              )}
              <span className={'fab-list-box-item-content-additional-info'}>
                <LocationSelectorAdditionalInfo nearestSlot={lt.nearestSlot} />
              </span>
            </div>
          </SelectItem>
        );
      })}
    </MobileSelect>
  );
};

export default LocationSelector;
